import React, { useState } from 'react';
import { Download, X } from 'lucide-react';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  pdfGenerating: boolean;
  onExportPDF: () => Promise<void>;
  onSendEmail: (email: string) => Promise<void>;
  exportEmailStatus: 'idle' | 'sending' | 'success' | 'error';
  exportEmailError: string;
  exportError: string;
}

const ExportModal: React.FC<ExportModalProps> = ({
  isOpen,
  onClose,
  pdfGenerating,
  onExportPDF,
  onSendEmail,
  exportEmailStatus,
  exportEmailError,
  exportError,
}) => {
  const [exportEmailTo, setExportEmailTo] = useState('');
  const [showTooltip, setShowTooltip] = useState('');

  if (!isOpen) return null;

  const handleSendEmail = async () => {
    if (!exportEmailTo) return;
    await onSendEmail(exportEmailTo);
  };

  const handleClose = () => {
    setExportEmailTo('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black bg-opacity-30">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
        <button
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
          onClick={handleClose}
          aria-label="Close"
          onMouseEnter={() => setShowTooltip('closeExport')}
          onMouseLeave={() => setShowTooltip('')}
        >
          <X className="h-5 w-5" />
        </button>
        {showTooltip === 'closeExport' && (
          <div className="absolute right-10 top-2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-20">
            Close export modal
          </div>
        )}
        <h2 className="text-lg font-semibold mb-4 text-blue-800 flex items-center">
          <Download className="h-5 w-5 mr-2" /> Export Simulation Analysis
        </h2>
        <div className="space-y-3">
          <div className="relative">
            <button
              className={`w-full py-2 rounded bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center ${
                pdfGenerating ? 'opacity-60 cursor-not-allowed' : ''
              }`}
              disabled={pdfGenerating}
              onClick={onExportPDF}
              onMouseEnter={() => setShowTooltip('exportPDF')}
              onMouseLeave={() => setShowTooltip('')}
            >
              {pdfGenerating ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5 mr-2 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8z"
                    ></path>
                  </svg>
                  Preparing PDF...
                </>
              ) : (
                'Export as PDF'
              )}
            </button>
            {showTooltip === 'exportPDF' && (
              <div className="absolute left-1/2 -translate-x-1/2 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-20">
                Download simulation analysis as PDF
              </div>
            )}
          </div>
          <div className="text-center text-gray-400 text-xs">or</div>
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Send PDF to Email
            </label>
            <input
              type="email"
              className="w-full border border-gray-300 rounded px-3 py-2 mb-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="recipient@example.com"
              value={exportEmailTo}
              onChange={(e) => setExportEmailTo(e.target.value)}
              disabled={exportEmailStatus === 'sending'}
            />
            <div className="relative">
              <button
                className={`w-full py-2 rounded bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors ${
                  exportEmailStatus === 'sending' || pdfGenerating
                    ? 'opacity-60 cursor-not-allowed'
                    : ''
                }`}
                disabled={exportEmailStatus === 'sending' || !exportEmailTo || pdfGenerating}
                onClick={handleSendEmail}
                onMouseEnter={() => setShowTooltip('sendEmail')}
                onMouseLeave={() => setShowTooltip('')}
              >
                {exportEmailStatus === 'sending' ? 'Sending...' : 'Send PDF to Email'}
              </button>
              {showTooltip === 'sendEmail' && (
                <div className="absolute left-1/2 -translate-x-1/2 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-20">
                  Send PDF to the specified email address
                </div>
              )}
            </div>
            {exportEmailStatus === 'success' && (
              <div className="mt-2 text-green-600 text-sm font-medium">
                Email sent successfully!
              </div>
            )}
            {exportEmailStatus === 'error' && (
              <div className="mt-2 text-red-600 text-sm font-medium">{exportEmailError}</div>
            )}
          </div>
          {exportError && (
            <div className="mt-2 text-red-600 text-sm font-medium">{exportError}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
